import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { toast } from "sonner";
import {
  X,
  Link as LinkIcon,
  FileText,
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Quote,
  Undo2,
  Redo2,
  Link2,
} from "lucide-react";
import { tilesApi } from "../../api";
import type { Tile } from "../../types";

const schema = z
  .object({
    id: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/)
      .optional(),
    name: z.string().min(1).max(128),
    description: z.string().max(512).default(""),
    icon: z.string().min(1).max(64),
    kind: z.enum(["link", "content"]),
    url: z.string().max(2048).optional(),
    content_html: z.string().max(50_000).optional(),
    open_in_new_tab: z.boolean().default(true),
    badge: z.string().max(32).nullable().optional(),
    tagsCsv: z.string().max(640).default(""),
    order: z.coerce.number().int().nonnegative(),
    hidden: z.boolean().default(false),
  })
  .superRefine((v, ctx) => {
    if (v.kind === "link") {
      if (!v.url || v.url.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["url"],
          message: "URL is required for link tiles",
        });
      } else {
        try { new URL(v.url); } catch {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["url"],
            message: "must be a valid URL",
          });
        }
      }
    } else {
      const stripped = (v.content_html ?? "").replace(/<[^>]*>/g, "").trim();
      if (!stripped) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["content_html"],
          message: "Content is required for content tiles",
        });
      }
    }
  });
type FormValues = z.infer<typeof schema>;

interface Props {
  sectionId: string;
  existing: Tile | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function TileForm({ sectionId, existing, onClose, onSaved }: Props) {
  const isEdit = !!existing;
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: existing
      ? {
          id: existing.id,
          name: existing.name,
          description: existing.description,
          icon: existing.icon,
          kind: existing.kind ?? "link",
          url: existing.url ?? "",
          content_html: existing.content_html ?? "",
          open_in_new_tab: existing.open_in_new_tab,
          badge: existing.badge ?? "",
          tagsCsv: (existing.tags ?? []).join(", "),
          order: existing.order,
          hidden: existing.hidden,
        }
      : {
          name: "",
          description: "",
          icon: "arrow-up-right",
          kind: "link",
          url: "https://",
          content_html: "",
          open_in_new_tab: true,
          badge: "",
          tagsCsv: "",
          order: 999,
          hidden: false,
        },
  });

  const kind = watch("kind");

  async function onSubmit(v: FormValues) {
    const tags = v.tagsCsv
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 20);
    const payload = {
      name: v.name,
      description: v.description,
      icon: v.icon,
      kind: v.kind,
      url: v.kind === "link" ? v.url : null,
      content_html: v.kind === "content" ? v.content_html : null,
      open_in_new_tab: v.open_in_new_tab,
      badge: v.badge && v.badge.length ? v.badge : null,
      tags,
      order: v.order,
      hidden: v.hidden,
    };
    try {
      if (isEdit && existing) {
        await tilesApi.update(existing.id, payload);
        toast.success("Saved");
      } else {
        await tilesApi.create({ id: v.id || undefined, section_id: sectionId, ...payload });
        toast.success("Service created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Save failed");
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm grid place-items-center p-4 animate-fade-in">
      <div className="card !rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-modal animate-scale-in">
        <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="display text-[18px] font-medium tracking-tight">
            {isEdit ? "Edit service" : "New service"}
          </h2>
          <button onClick={onClose} className="btn-icon" aria-label="close">
            <X className="size-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 space-y-4">
          <div>
            <label className="label">Service type</label>
            <div className="grid grid-cols-2 gap-2">
              <KindOption
                label="External link"
                description="Opens a URL"
                icon={<LinkIcon className="size-3.5" />}
                active={kind === "link"}
                onClick={() => setValue("kind", "link", { shouldValidate: true })}
              />
              <KindOption
                label="Inline content"
                description="Popup with content"
                icon={<FileText className="size-3.5" />}
                active={kind === "content"}
                onClick={() => setValue("kind", "content", { shouldValidate: true })}
              />
            </div>
          </div>



          <div>
            <label className="label">Name</label>
            <input className="input" {...register("name")} />
            {errors.name && <p className="text-[11.5px] text-accent-600 mt-1">{errors.name.message}</p>}
          </div>

          <div>
            <label className="label">Description</label>
            <textarea className="input" rows={2} {...register("description")} placeholder="One-line summary shown on the card" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Icon</label>
              <input className="input font-mono" {...register("icon")} placeholder="key-round" />
              <p className="text-[10.5px] text-ink-400 mt-1">lucide-react name</p>
            </div>
            <div>
              <label className="label">Badge (optional)</label>
              <input className="input" {...register("badge")} placeholder="New" />
            </div>
          </div>

          {kind === "link" ? (
            <>
              <div>
                <label className="label">URL</label>
                <input className="input" {...register("url")} placeholder="https://example.com" />
                {errors.url && <p className="text-[11.5px] text-accent-600 mt-1">{errors.url.message}</p>}
              </div>
              <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                <input type="checkbox" {...register("open_in_new_tab")} /> Open in new tab
              </label>
            </>
          ) : (
            <div>
              <label className="label">Content</label>
              <Controller
                control={control}
                name="content_html"
                render={({ field }) => (
                  <RichEditor initialHtml={field.value ?? ""} onChange={field.onChange} />
                )}
              />
              {errors.content_html && (
                <p className="text-[11.5px] text-accent-600 mt-1">{errors.content_html.message}</p>
              )}
              <p className="text-[10.5px] text-ink-400 mt-1">
                Shown in a popup when the service is clicked.
              </p>
            </div>
          )}

          <div>
            <label className="label">Tags (comma-separated)</label>
            <input className="input" {...register("tagsCsv")} placeholder="ai, finance, claude" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Order</label>
              <input type="number" className="input" {...register("order")} />
            </div>
            <div className="pt-7">
              <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                <input type="checkbox" {...register("hidden")} /> Hidden from viewer
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-ink-100 mt-4">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? "SavingÃ¢â‚¬Â¦" : isEdit ? "Save changes" : "Create service"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function KindOption({
  label,
  description,
  icon,
  active,
  onClick,
}: {
  label: string;
  description: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-lg border p-3 transition-colors ${
        active
          ? "border-ink-900 bg-cream-50 ring-1 ring-ink-900/10"
          : "border-ink-200 hover:border-ink-300"
      }`}
    >
      <div className="flex items-center gap-1.5 text-[13px] font-medium text-ink-900">
        {icon} {label}
      </div>
      <p className="text-[11px] text-ink-500 mt-0.5">{description}</p>
    </button>
  );
}

function RichEditor({
  initialHtml,
  onChange,
}: {
  initialHtml: string;
  onChange: (html: string) => void;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
    ],
    content: initialHtml || "",
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[200px] focus:outline-none px-4 py-3 prose-headings:font-display prose-headings:italic prose-headings:tracking-tight prose-headings:font-medium prose-a:text-accent",
      },
    },
  });

  if (!editor) return null;

  const setLink = () => {
    const previous = editor.getAttributes("link").href ?? "";
    const url = window.prompt("URL (leave empty to remove)", previous);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border border-ink-200 rounded-lg overflow-hidden bg-white">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-ink-100 bg-cream-50 flex-wrap">
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold">
          <Bold className="size-3" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic">
          <Italic className="size-3" />
        </ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2">
          <Heading2 className="size-3" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Heading 3">
          <Heading3 className="size-3" />
        </ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list">
          <List className="size-3" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list">
          <ListOrdered className="size-3" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote">
          <Quote className="size-3" />
        </ToolBtn>
        <Divider />
        <ToolBtn onClick={setLink} active={editor.isActive("link")} title="Link">
          <Link2 className="size-3" />
        </ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Undo">
          <Undo2 className="size-3" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Redo">
          <Redo2 className="size-3" />
        </ToolBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolBtn({
  onClick,
  active,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`size-6 rounded inline-flex items-center justify-center transition-colors ${
        active ? "bg-ink-900 text-cream-50" : "text-ink-600 hover:bg-cream-200 hover:text-ink-900"
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-4 bg-ink-200 mx-0.5" />;
}
